import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { pendingPreview } from '../../utils/businessPreview';

/** Keep authenticated accounts out of auth screens without stranding a
 * partially-created account that has not acquired a workspace yet. */
const AuthRedirect = ({ children }) => {
  const { isAuthenticated, user, isInitialized, requiresTenantSelection, memberships } = useAuthStore();
  const location = useLocation();

  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    const needsWorkspacePick = Boolean(
      requiresTenantSelection
      || (!user?.tenant_id && Array.isArray(memberships) && memberships.length > 1)
    );
    if (needsWorkspacePick) return children;
    // Superadmins keep PLATFORM_TENANT_ID as their legacy tenant_id, so this
    // has to come first: checking tenant_id above it would send every
    // superadmin into the platform tenant's setup wizard and leave the
    // platform dashboard unreachable.
    if (user?.is_superuser || user?.role === 'superadmin') {
      return <Navigate to="/platform/dashboard" replace />;
    }
    if (user?.tenant_id) {
      if (pendingPreview()) return <Navigate to="/keep-agent" replace />;
      return <Navigate to={`/t/${user.tenant_id}/get-started`} replace />;
    }

    // A partially-created browser session has authenticated successfully but
    // has no workspace yet. Sending an explicit signup visit to /welcome
    // strands the visitor outside the form they just asked to open.
    if (location.pathname === '/auth/signup') return children;
    return <Navigate to="/auth/signup" replace />;
  }

  return children;
};

export default AuthRedirect;
